import React, { useEffect } from 'react'
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Form,
  FormGroup,
  FormFeedback,
  Card,
  CardBody
} from 'reactstrap'
import { IUser } from './index'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import * as Yup from 'yup'

interface IProps {
  userRecord: IUser | undefined
  isOpen: boolean
  editMode: boolean
  setOpenPopup: (arg: boolean) => void
  handleCreateOrUpdateUser: (arg: any) => Promise<string>
  canEdit: boolean
}

// Pages that can have permissions assigned
// ⚠️ Bu liste backend'deki `verifyAdmin.check("<Sayfa>", ...)` adlarıyla
// BİREBİR aynı olmak zorundadır — eşleşme metinle yapılır, farklı yazım
// sessizce "yetki yok" demektir. Sapmayı yakalayan test:
//   SocialBooth-Backend/tests/admin-permissions.test.js
const AVAILABLE_PAGES = [
  'Sales',
  'Booths',
  'Locations',
  'Services',
  'Promotions',
  'Promotion Booths',
  'Backgrounds',
  'Effects/Filters',
  // 2026-08-17: backend bu adı ZORLUYORDU ama listede yoktu → hiçbir operatöre
  // verilemiyordu, yani operatörler aksesuar düzenlemeye kalıcı olarak kapalıydı.
  'Accessories',
  // 2026-08-17: bu iki uç generic verifyAdmin kullanıyordu; sadece-görüntüleme
  // yetkisi olan operatör kabin sürelerini ve metinlerini değiştirebiliyordu.
  // Granüler kontrole çevrildi, dolayısıyla yetkinin verilebilir olması gerekir.
  'Timer Settings',
  'Translations',
  // ⏳ GECICI — kamera ayar paneli (2026-08-04), yetkilendirme 2026-08-05
  // 🔻 Kaldirma recetesi: SocialBooth-Backend/KALDIR-kamera-ayar-paneli.md
  'Camera Settings',
  'Users'
]

const PERMISSION_OPTIONS = [
  { name: 'View', value: 'canView' },
  { name: 'Create', value: 'canCreate' },
  { name: 'Edit', value: 'canEdit' },
  { name: 'Delete', value: 'canDelete' }
]

const UserModal: React.FC<IProps> = ({
  userRecord,
  isOpen,
  editMode,
  handleCreateOrUpdateUser,
  setOpenPopup,
  canEdit
}) => {
  const hasEdit = (editMode && userRecord) || false

  const yupValidates = hasEdit
    ? Yup.object({
        fullName: Yup.string().required('Please Enter Full Name'),
        email: Yup.string().email('Invalid email').required('Please Enter Email'),
        // Edit mode: password is optional. Leave blank to keep the current one.
        // If provided, must satisfy the backend policy (min 8, 1 uppercase, 1 number).
        password: Yup.string().test(
          'password-policy',
          'Min 8 characters, incl. 1 uppercase letter and 1 number',
          (value) => !value || (value.length >= 8 && /[A-Z]/.test(value) && /[0-9]/.test(value))
        )
      })
    : Yup.object({
        fullName: Yup.string().required('Please Enter Full Name'),
        email: Yup.string().email('Invalid email').required('Please Enter Email'),
        password: Yup.string().min(6, 'Min 6 characters').required('Please Enter Password')
      })

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: hasEdit ? userRecord?._id : '',
      fullName: hasEdit ? ((userRecord as any)?.fullname || userRecord?.fullName || '') : '',
      email: hasEdit ? userRecord?.email : '',
      type: hasEdit ? (userRecord as any)?.type ?? ((userRecord as any)?.role === 'admin' || (userRecord as any)?.role === 'superAdmin' ? 1 : 2) : 2,
      isActive: hasEdit ? userRecord?.isActive !== false : true,
      password: '',
      pagePermissions: hasEdit ? ((userRecord as any)?.pagePermissions || []) : []
    },
    validationSchema: yupValidates,
    onSubmit: (values) => {
      handleSubmit(values)
    }
  })

  useEffect(() => {
    if (isOpen) {
      formik.resetForm()
    }
  }, [isOpen])

  const togglePopup = () => {
    setOpenPopup(!isOpen)
  }

  const handleOnChange = (e: { target: { name: any; value: any } }) => {
    formik.setFieldValue(e.target.name, e.target.value)
  }

  const handleSubmit = async (values: any) => {
    const roleMap: Record<number, string> = { 1: 'admin', 2: 'operator' }
    const role = roleMap[Number(values.type)] || 'operator'
    const params: any = {
      fullname: values.fullName,
      email: values.email,
      role,
      isActive: values.isActive
    }

    // Only send pagePermissions for operator role
    if (role === 'operator') {
      params.pagePermissions = values.pagePermissions
    } else {
      params.pagePermissions = []
    }

    if (editMode) {
      params._id = values.id
      // Only send the password when the admin actually typed a new one.
      // Empty => backend keeps the current password unchanged.
      if (values.password) {
        params.password = values.password
      }
    } else {
      params.password = values.password
    }

    const message = await handleCreateOrUpdateUser(params)
    if (message === 'SUCCESSFULL') {
      toast.success(editMode ? 'Updated user successfully' : 'Created new user successfully')
      setOpenPopup(false)
    } else {
      toast.error(message)
    }
  }

  const getPermissionValue = (page: string, field: string): boolean => {
    const perms = formik.values.pagePermissions || []
    const perm = perms.find((p: any) => p.page === page)
    return perm ? perm[field] === true : false
  }

  const handlePermissionChange = (page: string, field: string, value: boolean) => {
    const perms = [...(formik.values.pagePermissions || [])]
    const idx = perms.findIndex((p: any) => p.page === page)

    if (idx >= 0) {
      perms[idx] = { ...perms[idx], [field]: value }
      // If canView is disabled, disable all other permissions
      if (field === 'canView' && !value) {
        perms[idx].canCreate = false
        perms[idx].canEdit = false
        perms[idx].canDelete = false
      }
    } else {
      perms.push({
        page,
        canView: field === 'canView' ? value : false,
        canCreate: field === 'canCreate' ? value : false,
        canEdit: field === 'canEdit' ? value : false,
        canDelete: field === 'canDelete' ? value : false
      })
    }

    formik.setFieldValue('pagePermissions', perms)
  }

  const toggleAllPermissions = (page: string) => {
    const perms = [...(formik.values.pagePermissions || [])]
    const idx = perms.findIndex((p: any) => p.page === page)
    const allEnabled = idx >= 0 && perms[idx].canView && perms[idx].canCreate && perms[idx].canEdit && perms[idx].canDelete

    if (idx >= 0) {
      perms[idx] = { ...perms[idx], canView: !allEnabled, canCreate: !allEnabled, canEdit: !allEnabled, canDelete: !allEnabled }
    } else {
      perms.push({ page, canView: true, canCreate: true, canEdit: true, canDelete: true })
    }
    formik.setFieldValue('pagePermissions', perms)
  }

  const isOperator = Number(formik.values.type) === 2

  return (
    <React.Fragment>
      <Modal isOpen={isOpen} toggle={togglePopup} scrollable={true} id='userModal' size={isOperator ? 'lg' : undefined}>
        <ModalHeader className='modal-title' id='userModalTitle' toggle={togglePopup}>
          {editMode ? 'User Record Details' : 'Create a new User'}
        </ModalHeader>
        <ModalBody>
          <Form
            className='needs-validation'
            onSubmit={(e) => {
              e.preventDefault()
              formik.handleSubmit()
              return false
            }}
          >
            <Row>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label className='form-label mb-1 fw-semibold'>Full Name</Label>
                  <Input
                    type='text'
                    maxLength={50}
                    name='fullName'
                    placeholder='Full Name'
                    value={formik.values.fullName}
                    onChange={handleOnChange}
                    invalid={formik.touched.fullName && formik.errors.fullName ? true : false}
                  />
                  {formik.touched.fullName && formik.errors.fullName ? (
                    <FormFeedback type='invalid'>{formik.errors.fullName}</FormFeedback>
                  ) : null}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label className='form-label mb-1 fw-semibold'>Email</Label>
                  <Input
                    type='email'
                    maxLength={50}
                    name='email'
                    placeholder='Email'
                    value={formik.values.email}
                    onChange={handleOnChange}
                    invalid={formik.touched.email && formik.errors.email ? true : false}
                  />
                  {formik.touched.email && formik.errors.email ? (
                    <FormFeedback type='invalid'>{formik.errors.email}</FormFeedback>
                  ) : null}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label className='form-label mb-1 fw-semibold'>Role</Label>
                  <select
                    className='form-select'
                    name='type'
                    value={formik.values.type}
                    onChange={handleOnChange}
                  >
                    <option value={1}>Admin</option>
                    <option value={2}>User (Operator)</option>
                  </select>
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label className='form-label mb-1 fw-semibold'>
                    {editMode ? 'New Password' : 'Password'}
                  </Label>
                  <Input
                    type='password'
                    maxLength={50}
                    name='password'
                    autoComplete='new-password'
                    placeholder={editMode ? 'New password' : 'Password'}
                    value={formik.values.password}
                    onChange={handleOnChange}
                    invalid={formik.touched.password && formik.errors.password ? true : false}
                  />
                  {formik.touched.password && formik.errors.password ? (
                    <FormFeedback type='invalid'>{formik.errors.password}</FormFeedback>
                  ) : editMode ? (
                    <small className='text-muted'>
                      Leave blank to keep the current password · Min 8 chars, 1 uppercase, 1 number
                    </small>
                  ) : null}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <div className='form-check'>
                    <Input
                      className='form-check-input'
                      type='checkbox'
                      id='is-active'
                      checked={formik.values.isActive}
                      onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                    />
                    <Label className='form-check-label fw-medium' htmlFor='is-active'>
                      Active
                    </Label>
                  </div>
                </Col>
              </FormGroup>

              {/* Page Permissions - Only for Operator role */}
              {isOperator && (
                <FormGroup>
                  <Col md={12} className='mb-3'>
                    <Label className='form-label mb-1 fw-semibold'>Page Permissions</Label>
                    <Card className='border'>
                      <CardBody className='p-3'>
                        <div className='table-responsive'>
                          <table className='table table-sm table-bordered mb-0 align-middle'>
                            <thead className='table-light'>
                              <tr>
                                <th style={{ width: '180px' }}>Page</th>
                                {PERMISSION_OPTIONS.map((opt) => (
                                  <th key={opt.value} className='text-center' style={{ width: '80px' }}>
                                    {opt.name}
                                  </th>
                                ))}
                                <th className='text-center' style={{ width: '80px' }}>All</th>
                              </tr>
                            </thead>
                            <tbody>
                              {AVAILABLE_PAGES.map((page) => (
                                <tr key={page}>
                                  <td className='fw-medium'>{page}</td>
                                  {PERMISSION_OPTIONS.map((opt) => (
                                    <td key={opt.value} className='text-center'>
                                      <Input
                                        className='form-check-input'
                                        type='checkbox'
                                        checked={getPermissionValue(page, opt.value)}
                                        disabled={opt.value !== 'canView' && !getPermissionValue(page, 'canView')}
                                        onChange={(e) => handlePermissionChange(page, opt.value, e.target.checked)}
                                      />
                                    </td>
                                  ))}
                                  <td className='text-center'>
                                    <Button
                                      color='soft-primary'
                                      size='sm'
                                      className='py-0 px-1'
                                      onClick={() => toggleAllPermissions(page)}
                                    >
                                      <i className='ri-checkbox-multiple-line'></i>
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </FormGroup>
              )}
            </Row>

            <div className='modal-footer'>
              <Button color='light' onClick={() => setOpenPopup(false)}>
                Close
              </Button>
              {canEdit && (
                <Button color='primary' type='submit' disabled={!formik.dirty && editMode}>
                  Save
                </Button>
              )}
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default UserModal
